<template>
  <div :class="rootClasses">
    <div class="payment-flow__aurora payment-flow__aurora--left"></div>
    <div class="payment-flow__aurora payment-flow__aurora--right"></div>

    <div class="payment-flow__shell">
      <transition name="payment-flow-switch" mode="out-in">
        <div v-if="!resultVisible" key="loading" class="payment-flow__loading">
          <div class="payment-flow__card-wrap">
            <div class="payment-flow__card-glow"></div>
            <div class="payment-flow__card">
              <div class="payment-flow__chip"></div>
              <div class="payment-flow__signal">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <div class="payment-flow__digits">
                <span v-for="n in 4" :key="n"></span>
              </div>
            </div>
          </div>

          <div class="payment-flow__title">
            {{ currentStatus
            }}<span class="payment-flow__dots">{{ animatedDots }}</span>
          </div>

          <div class="payment-flow__progress">
            <div
              class="payment-flow__progress-fill"
              :style="{ width: `${progress}%` }"
            ></div>
          </div>

          <div class="payment-flow__wait">
            {{ copy.waitLabel }} {{ remainingTime }}
          </div>

          <div class="payment-flow__secure">
            <span class="payment-flow__shield"></span>
            <span>{{ copy.secureLabel }}</span>
          </div>
        </div>

        <div v-else key="result" class="payment-flow__result">
          <div
            class="payment-flow__result-icon"
            :class="resultSuccess ? 'is-success' : 'is-error'"
          >
            <span
              v-if="resultSuccess"
              class="payment-flow__result-mark payment-flow__result-mark--check"
            ></span>
            <span
              v-else
              class="payment-flow__result-mark payment-flow__result-mark--cross"
            ></span>
          </div>

          <div
            class="payment-flow__result-title"
            :class="resultSuccess ? 'is-success' : 'is-error'"
          >
            {{ resultTitle }}
          </div>

          <div class="payment-flow__result-message">
            {{ resultMessage }}
          </div>

          <div class="payment-flow__divider"></div>

          <div class="payment-flow__hint">
            {{ resultHint }}
          </div>

          <button
            type="button"
            class="payment-flow__action"
            @click="$emit('action')"
          >
            {{ actionText }}
          </button>
        </div>
      </transition>

      <button
        type="button"
        class="payment-flow__good-action"
        @click="openDonora"
      >
        <span class="payment-flow__good-content">
          <span class="payment-flow__good-heart" aria-hidden="true">
            <svg viewBox="0 0 64 64">
              <path
                d="M32 52S10 39 10 23c0-7 5-12 12-12 4 0 8 2 10 6 2-4 6-6 10-6 7 0 12 5 12 12 0 16-22 29-22 29Z"
              />
            </svg>
          </span>
          <span>{{ goodActionText }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import Donora from "@/plugins/api/factories/Donora";

const DONORA_LINK =
  "https://donora.help/app?utm_source=zhytomyr_energo&utm_medium=partner&utm_campaign=ua_utilities_app_install&utm_content=payment_flow";

export default {
  name: "PaymentFlowAnimation",
  props: {
    result: {
      type: Object,
      default: null,
    },
    flow: {
      type: String,
      default: "payment",
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    successActionLabel: {
      type: String,
      default: "",
    },
    errorActionLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      progress: this.result ? 100 : 8,
      statusIndex: 0,
      dotCount: 2,
      remainingSeconds: 120,
      resultVisible: !!this.result,
      progressTimer: null,
      statusTimer: null,
      dotsTimer: null,
      clockTimer: null,
      revealTimer: null,
    };
  },
  computed: {
    localeKey() {
      if (this.$i18n && this.$i18n.locale === "en") return "en";
      if (this.$i18n && this.$i18n.locale === "ru") return "ru";
      return "uk";
    },
    copy() {
      return {
        uk: {
          statuses: [
            "Підключення до банку",
            "Перевірка статусу",
            "Обробка операції",
          ],
          waitLabel: "Очікуйте відповідь",
          secureLabel: "Захищене з'єднання з банком",
          paymentSuccessTitle: "Оплату прийнято!",
          paymentErrorTitle: "Платіж відхилено",
          cardSuccessTitle: "Картку збережено!",
          cardAlreadyTitle: "Картку вже додано",
          cardErrorTitle: "Картку не додано",
          paymentSuccessHint: "Дякуємо за оплату",
          paymentErrorHint: "Спробуйте ще раз або оберіть іншу картку",
          cardSuccessHint: "Картка готова до швидкої оплати",
          cardErrorHint: "Перевірте дані та спробуйте ще раз",
          done: "Готово",
          retry: "Спробувати ще раз",
        },
        ru: {
          statuses: [
            "Подключение к банку",
            "Проверка статуса",
            "Обработка операции",
          ],
          waitLabel: "Ожидайте ответ",
          secureLabel: "Защищенное соединение с банком",
          paymentSuccessTitle: "Оплата принята!",
          paymentErrorTitle: "Платеж отклонен",
          cardSuccessTitle: "Карта сохранена!",
          cardAlreadyTitle: "Карта уже добавлена",
          cardErrorTitle: "Карта не добавлена",
          paymentSuccessHint: "Спасибо за оплату",
          paymentErrorHint: "Попробуйте еще раз или выберите другую карту",
          cardSuccessHint: "Карта готова к быстрой оплате",
          cardErrorHint: "Проверьте данные и попробуйте еще раз",
          done: "Готово",
          retry: "Попробовать снова",
        },
        en: {
          statuses: [
            "Connecting to bank",
            "Checking status",
            "Processing request",
          ],
          waitLabel: "Waiting for response",
          secureLabel: "Secure bank connection",
          paymentSuccessTitle: "Payment accepted!",
          paymentErrorTitle: "Payment declined",
          cardSuccessTitle: "Card saved!",
          cardAlreadyTitle: "Card already added",
          cardErrorTitle: "Card not saved",
          paymentSuccessHint: "Thank you for your payment",
          paymentErrorHint: "Try again or use another card",
          cardSuccessHint: "Card is ready for quick payments",
          cardErrorHint: "Check the card details and try again",
          done: "Done",
          retry: "Try again",
        },
      }[this.localeKey];
    },
    rootClasses() {
      return [
        "payment-flow",
        this.overlay ? "payment-flow--overlay" : "payment-flow--page",
      ];
    },
    currentStatus() {
      return this.copy.statuses[this.statusIndex] || this.copy.statuses[0];
    },
    animatedDots() {
      return ".".repeat(this.dotCount);
    },
    remainingTime() {
      const minutes = Math.floor(this.remainingSeconds / 60);
      const seconds = String(this.remainingSeconds % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    resultSuccess() {
      return !!(this.result && this.result.success);
    },
    isAlreadyAdded() {
      return !!(this.result && this.result.alreadyAdded);
    },
    resultTitle() {
      if (this.flow === "card") {
        if (this.resultSuccess && this.isAlreadyAdded) {
          return this.copy.cardAlreadyTitle;
        }
        return this.resultSuccess
          ? this.copy.cardSuccessTitle
          : this.copy.cardErrorTitle;
      }

      return this.resultSuccess
        ? this.copy.paymentSuccessTitle
        : this.copy.paymentErrorTitle;
    },
    resultMessage() {
      if (this.result && (this.result.message || this.result.error)) {
        return this.result.message || this.result.error;
      }
      if (this.flow === "card") {
        return this.resultSuccess
          ? this.copy.cardSuccessHint
          : this.copy.cardErrorHint;
      }
      return this.resultSuccess
        ? this.copy.paymentSuccessHint
        : this.copy.paymentErrorHint;
    },
    resultHint() {
      if (this.flow === "card") {
        return this.resultSuccess
          ? this.copy.cardSuccessHint
          : this.copy.cardErrorHint;
      }
      return this.resultSuccess
        ? this.copy.paymentSuccessHint
        : this.copy.paymentErrorHint;
    },
    actionText() {
      if (this.resultSuccess) {
        return this.successActionLabel || this.copy.done;
      }
      return this.errorActionLabel || this.copy.retry;
    },
    goodActionText() {
      return this.$t("donora.goodDeedButton");
    },
  },
  watch: {
    result: {
      immediate: true,
      handler(next, prev) {
        if (next && !prev) {
          this.finishLoadingState();
          return;
        }
        if (!next) {
          this.resetLoadingState();
          this.startLoadingState();
        }
      },
    },
  },
  mounted() {
    if (!this.result) {
      this.startLoadingState();
    }
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    startLoadingState() {
      if (this.resultVisible) {
        return;
      }
      this.clearTimers();
      this.progressTimer = setInterval(() => {
        if (this.progress < 88) {
          this.progress = Math.min(this.progress + 0.55, 88);
        }
      }, 110);
      this.statusTimer = setInterval(() => {
        this.statusIndex = (this.statusIndex + 1) % this.copy.statuses.length;
      }, 3200);
      this.dotsTimer = setInterval(() => {
        this.dotCount = this.dotCount >= 3 ? 1 : this.dotCount + 1;
      }, 420);
      this.clockTimer = setInterval(() => {
        if (this.remainingSeconds > 0) {
          this.remainingSeconds -= 1;
        }
      }, 1000);
    },
    finishLoadingState() {
      this.clearTimers();
      this.progress = 100;
      this.revealTimer = setTimeout(() => {
        this.resultVisible = true;
      }, 280);
    },
    resetLoadingState() {
      this.clearTimers();
      this.progress = 8;
      this.statusIndex = 0;
      this.dotCount = 2;
      this.remainingSeconds = 120;
      this.resultVisible = false;
    },
    clearTimers() {
      clearInterval(this.progressTimer);
      clearInterval(this.statusTimer);
      clearInterval(this.dotsTimer);
      clearInterval(this.clockTimer);
      clearTimeout(this.revealTimer);
      this.progressTimer = null;
      this.statusTimer = null;
      this.dotsTimer = null;
      this.clockTimer = null;
      this.revealTimer = null;
    },
    async openDonora() {
      const link = DONORA_LINK;

      try {
        await Donora.link(this.getDonoraPayload(link));
      } catch (error) {
        console.error("Donora link tracking error:", error);
      } finally {
        this.openExternalUrl(link);
      }
    },
    getDonoraPayload(link) {
      const account =
        (this.$store.state.Auth.account && this.$store.state.Auth.account.ls) ||
        this.$route.params.account ||
        "";

      return {
        account_number: account,
        address: this.getAccountAddress(),
        link,
      };
    },
    getAccountAddress() {
      const account = this.$store.state.Auth.account;

      if (!account) {
        return "";
      }

      return [
        account.streetType,
        account.streetName,
        account.building,
        account.appartment
          ? `${this.$t("components.accounts.apartment")} ${account.appartment}`
          : "",
        account.city,
      ]
        .filter(Boolean)
        .join(", ");
    },
    openExternalUrl(url) {
      if (typeof this.nativeSendEvent === "function") {
        this.nativeSendEvent("url", url);
        return;
      }

      window.open(url, "_blank", "noopener");
    },
  },
};
</script>

<style scoped>
.payment-flow {
  position: relative;
  width: 100%;
  min-height: 100%;
  overflow: hidden;
  background: radial-gradient(
      circle at 18% 24%,
      rgba(77, 110, 255, 0.16),
      transparent 20%
    ),
    radial-gradient(
      circle at 82% 56%,
      rgba(37, 167, 214, 0.14),
      transparent 24%
    ),
    linear-gradient(180deg, #060d18 0%, #09111d 52%, #070d17 100%);
}

.payment-flow--overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
}

.payment-flow--page {
  min-height: calc(100vh - 96px);
}

.payment-flow__aurora {
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  filter: blur(18px);
  opacity: 0.8;
  pointer-events: none;
}

.payment-flow__aurora--left {
  top: 16%;
  left: -48px;
  background: radial-gradient(
    circle,
    rgba(82, 123, 255, 0.28),
    transparent 68%
  );
}

.payment-flow__aurora--right {
  top: 28%;
  right: -52px;
  background: radial-gradient(
    circle,
    rgba(74, 219, 255, 0.18),
    transparent 68%
  );
}

.payment-flow__shell {
  position: relative;
  z-index: 1;
  min-height: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 14px 24px;
  box-sizing: border-box;
}

.payment-flow__loading,
.payment-flow__result {
  width: min(100%, 280px);
  margin: 0 auto;
  text-align: center;
}

.payment-flow__card-wrap {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.payment-flow__card-glow {
  position: absolute;
  inset: auto;
  top: 8px;
  width: 166px;
  height: 92px;
  border-radius: 22px;
  background: radial-gradient(
    circle,
    rgba(80, 122, 255, 0.24),
    transparent 72%
  );
  filter: blur(20px);
  animation: pulseGlow 2.3s ease-in-out infinite;
}

.payment-flow__card {
  position: relative;
  width: min(100%, 168px);
  aspect-ratio: 1.58;
  border-radius: 18px;
  padding: 14px 14px 12px;
  box-sizing: border-box;
  background: radial-gradient(
      circle at 82% 18%,
      rgba(166, 211, 255, 0.28),
      transparent 24%
    ),
    linear-gradient(
      135deg,
      rgba(132, 183, 255, 0.2) 0%,
      rgba(82, 126, 255, 0.22) 52%,
      rgba(130, 231, 255, 0.14) 100%
    );
  border: 1px solid rgba(220, 239, 255, 0.3);
  box-shadow: 0 18px 34px rgba(11, 24, 58, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.24),
    inset 0 -1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  transform: translateY(0);
  animation: floatCard 3.4s ease-in-out infinite;
}

.payment-flow__card::before {
  content: "";
  position: absolute;
  inset: 8px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.08),
    transparent 58%
  );
  pointer-events: none;
}

.payment-flow__card::after {
  content: "";
  position: absolute;
  top: -10px;
  right: 18px;
  width: 54px;
  height: 120%;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(255, 255, 255, 0.2) 42%,
    transparent 100%
  );
  transform: rotate(22deg);
  opacity: 0.3;
}

.payment-flow__chip {
  width: 24px;
  height: 18px;
  border-radius: 6px;
  background: linear-gradient(180deg, #ffcf4e 0%, #f3b61c 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.24),
    0 3px 8px rgba(0, 0, 0, 0.18);
}

.payment-flow__signal {
  position: absolute;
  top: 16px;
  right: 14px;
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 16px;
}

.payment-flow__signal span {
  display: block;
  width: 3px;
  border-radius: 999px;
  background: rgba(240, 248, 255, 0.92);
}

.payment-flow__signal span:nth-child(1) {
  height: 6px;
}

.payment-flow__signal span:nth-child(2) {
  height: 9px;
}

.payment-flow__signal span:nth-child(3) {
  height: 13px;
}

.payment-flow__digits {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 12px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
}

.payment-flow__digits span {
  height: 5px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(248, 252, 255, 0.95),
    rgba(214, 233, 255, 0.58)
  );
}

.payment-flow__title {
  margin-bottom: 10px;
  color: #f4f7ff;
  font-size: clamp(15px, 3.1vw, 18px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.payment-flow__dots {
  display: inline-block;
  min-width: 16px;
  text-align: left;
}

.payment-flow__progress {
  width: min(100%, 232px);
  height: 5px;
  margin: 0 auto 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.payment-flow__progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #3c6cff 0%, #7ba3ff 100%);
  box-shadow: 0 0 20px rgba(89, 128, 255, 0.4);
  transition: width 0.28s ease;
}

.payment-flow__wait {
  margin-bottom: 12px;
  color: rgba(238, 243, 255, 0.88);
  font-size: 13px;
  font-weight: 500;
}

.payment-flow__secure {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  border-radius: 999px;
  background: linear-gradient(
    180deg,
    rgba(20, 48, 43, 0.82) 0%,
    rgba(18, 38, 37, 0.72) 100%
  );
  border: 1px solid rgba(90, 210, 126, 0.12);
  color: rgba(217, 255, 227, 0.92);
  font-size: 11px;
  font-weight: 500;
}

.payment-flow__shield {
  position: relative;
  width: 16px;
  height: 18px;
  background: linear-gradient(180deg, #61dd7f 0%, #33b95b 100%);
  clip-path: polygon(50% 0%, 100% 18%, 100% 58%, 50% 100%, 0% 58%, 0% 18%);
  box-shadow: 0 0 14px rgba(65, 204, 106, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.payment-flow__shield::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 6px;
  width: 6px;
  height: 4px;
  border-left: 2px solid rgba(16, 79, 35, 0.9);
  border-bottom: 2px solid rgba(16, 79, 35, 0.9);
  transform: rotate(-45deg);
}

.payment-flow__result-icon {
  position: relative;
  width: 70px;
  height: 70px;
  margin: 0 auto 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-flow__result-icon::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  opacity: 0.18;
}

.payment-flow__result-icon.is-success {
  border: 2px solid rgba(93, 219, 114, 0.8);
  box-shadow: 0 0 0 10px rgba(65, 190, 98, 0.06),
    0 0 28px rgba(74, 216, 110, 0.18);
}

.payment-flow__result-icon.is-success::before {
  background: radial-gradient(circle, rgba(73, 214, 107, 0.4), transparent 72%);
}

.payment-flow__result-icon.is-error {
  border: 2px solid rgba(255, 122, 122, 0.78);
  box-shadow: 0 0 0 10px rgba(235, 89, 89, 0.06),
    0 0 28px rgba(255, 109, 109, 0.16);
}

.payment-flow__result-icon.is-error::before {
  background: radial-gradient(
    circle,
    rgba(255, 116, 116, 0.34),
    transparent 72%
  );
}

.payment-flow__result-mark {
  position: relative;
  display: block;
}

.payment-flow__result-mark--check {
  width: 22px;
  height: 13px;
  border-left: 4px solid #62dd7b;
  border-bottom: 4px solid #62dd7b;
  transform: rotate(-45deg) translateY(-2px);
}

.payment-flow__result-mark--cross {
  width: 22px;
  height: 22px;
}

.payment-flow__result-mark--cross::before,
.payment-flow__result-mark--cross::after {
  content: "";
  position: absolute;
  top: 9px;
  left: 0;
  width: 22px;
  height: 3px;
  border-radius: 999px;
  background: #ff8787;
}

.payment-flow__result-mark--cross::before {
  transform: rotate(45deg);
}

.payment-flow__result-mark--cross::after {
  transform: rotate(-45deg);
}

.payment-flow__result-title {
  margin-bottom: 8px;
  font-size: clamp(18px, 3.8vw, 22px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.04em;
}

.payment-flow__result-title.is-success {
  color: #63d97d;
}

.payment-flow__result-title.is-error {
  color: #ff9a9a;
}

.payment-flow__result-message {
  max-width: 220px;
  margin: 0 auto;
  color: rgba(244, 247, 255, 0.9);
  font-size: 14px;
  line-height: 1.45;
}

.payment-flow__divider {
  width: 44px;
  height: 4px;
  margin: 14px auto 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
}

.payment-flow__hint {
  margin: 0 auto 16px;
  max-width: 210px;
  color: rgba(228, 234, 248, 0.8);
  font-size: 12px;
  line-height: 1.45;
}

.payment-flow__action {
  min-width: min(100%, 190px);
  height: 46px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #3868ff 0%, #5f90ff 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.02em;
  box-shadow: 0 14px 28px rgba(67, 111, 255, 0.18),
    0 0 20px rgba(67, 111, 255, 0.12);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.payment-flow__action:active {
  transform: translateY(1px);
}

.payment-flow__good-action {
  width: min(100%, 280px);
  height: 50px;
  margin: 18px auto 0;
  border: 0;
  padding: 0;
  border-radius: 999px;
  background: #4477f6;
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 10px 24px rgba(67, 111, 255, 0.16);
  outline-offset: 3px;
  transition: transform 0.16s ease, filter 0.16s ease;
}

.payment-flow__good-action:active {
  transform: translateY(1px);
}

.payment-flow__good-content {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
}

.payment-flow__good-heart {
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  animation: paymentGoodHeartPulse 1.35s ease-in-out infinite;
}

.payment-flow__good-heart svg {
  width: 25px;
  height: 25px;
  display: block;
  fill: none;
  stroke: #ffffff;
  stroke-width: 5;
  stroke-linejoin: round;
}

.payment-flow-switch-enter-active,
.payment-flow-switch-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.payment-flow-switch-enter,
.payment-flow-switch-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.985);
}

@keyframes floatCard {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes pulseGlow {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(0.96);
  }
  50% {
    opacity: 0.95;
    transform: scale(1.03);
  }
}

@keyframes paymentGoodHeartPulse {
  0%,
  100% {
    transform: scale(1);
  }
  48% {
    transform: scale(1.13);
  }
}

@media (max-width: 520px) {
  .payment-flow__shell {
    padding: 14px 10px 18px;
  }

  .payment-flow__loading,
  .payment-flow__result {
    width: min(100%, 248px);
  }

  .payment-flow__title {
    font-size: 14px;
  }

  .payment-flow__wait,
  .payment-flow__hint {
    font-size: 11px;
  }

  .payment-flow__result-message {
    font-size: 13px;
  }

  .payment-flow__result-title {
    font-size: 18px;
  }

  .payment-flow__card {
    width: min(100%, 150px);
    border-radius: 16px;
  }

  .payment-flow__card-glow {
    width: 146px;
    height: 82px;
  }

  .payment-flow__action {
    width: 100%;
    min-width: 0;
    font-size: 13px;
    height: 44px;
  }

  .payment-flow__good-action {
    width: min(100%, 248px);
    height: 44px;
    margin-top: 16px;
  }

  .payment-flow__good-content {
    height: 44px;
    gap: 10px;
    font-size: 15px;
  }

  .payment-flow__good-heart,
  .payment-flow__good-heart svg {
    width: 23px;
    height: 23px;
  }
}
</style>
